import React, { useEffect, useMemo, useState } from 'react';
import ModalShell from '../../../components/ModalShell';
import BalanceFanChart from './charts/BalanceFanChart';
import IncomeBreakdownChart from './charts/IncomeBreakdownChart';
import AssetClassChart from './charts/AssetClassChart';
import WithdrawalRateChart from './charts/WithdrawalRateChart';
import HouseholdWealthChart from './charts/HouseholdWealthChart';
import PortfolioBreakdownChart from './charts/PortfolioBreakdownChart';
import PortfolioAllocationSummary from './PortfolioAllocationSummary';

const CURRENT_YEAR = new Date().getFullYear();

const PERCENTILE_OPTIONS = [
  { value: 0, label: 'Pessimistic' },
  { value: 1, label: 'Conservative' },
  { value: 2, label: '50th Percentile' },
  { value: 3, label: 'Favourable' },
  { value: 4, label: 'Optimistic' },
];

// Helper to get age_1 from formData (handles both old age_1 and new birth_year format) or results
const getAge1 = (formData, results) => {
  if (results?.input_age_1) return results.input_age_1;
  if (formData?.age_1) return formData.age_1;
  if (formData?.birth_year_1) {
    const birthYear1 = formData.birth_year_1;
    const birthYear2 = formData.birth_year_2 || birthYear1;
    const age1Calc = CURRENT_YEAR - birthYear1;
    const age2Calc = CURRENT_YEAR - birthYear2;
    const isCouple = formData.couple === 'Yes';
    return isCouple ? Math.max(age1Calc, age2Calc) : age1Calc;
  }
  return 67;
};

const FrozenScenarioModal = ({ scenario, isOpen, onClose }) => {
  const [selectedPercentileIndex, setSelectedPercentileIndex] = useState(2);

  const results = scenario?.results;
  const formData = scenario?.formData;

  const incomeBreakdownData = useMemo(() => {
    if (!results) return null;

    const idx = selectedPercentileIndex;
    const portfolioIncome = results.portfolio_income_percentiles?.[idx] || [];
    const pensionIncome = results.pension_income_percentiles?.[idx] || [];
    const incomeStream = results.income_stream_percentiles?.[idx] || [];
    const otherIncomeTotal = results.other_income_percentiles?.[idx] || [];
    const lumpSumIncome = results.lump_sum_percentiles?.[idx] || [];

    const employmentAnnual = results.input_employment_income || 0;
    const otherAnnual = results.input_other_income || 0;
    const rentalAnnual = results.input_net_rental_income || 0;
    const workYears = results.input_work_years || 0;
    const otherIncomeYears = results.input_other_income_years || 0;
    const rentalIncomeYears = results.input_rental_income_years || 0;

    const years = results.years || [];

    const employmentIncome = years.map((y) => (y < workYears ? employmentAnnual : 0));
    const otherOnlyIncome = years.map((y) => (y < otherIncomeYears ? otherAnnual : 0));
    const rentalIncome = years.map((y) => (y < rentalIncomeYears ? rentalAnnual : 0));

    return {
      portfolio_income: portfolioIncome,
      pension_income: pensionIncome,
      employment_income: employmentIncome,
      other_income: otherOnlyIncome,
      rental_income: rentalIncome,
      income_stream: incomeStream,
      lump_sum_income: lumpSumIncome,
    };
  }, [results, selectedPercentileIndex]);

  const householdWealthData = useMemo(() => {
    if (!results) return null;

    const years = results.years || [];
    const portfolioBalance = results.balance_percentiles?.[2] || [];

    const propertyValue = results.input_investment_property_value || 0;
    const investmentProperty = years.map(() => propertyValue);

    const nonFinValue = results.input_non_financial_assets || 0;
    const nonFinancialAssets = years.map(() => nonFinValue);

    const annuityPurchaseAmount = results.input_annuity_purchase_amount || 0;
    const age1 = getAge1(formData, results);
    const annuityValue = years.map((y) => {
      if (annuityPurchaseAmount <= 0) return 0;
      const currentAge = age1 + y;
      if (currentAge >= 86) return 0;
      const yearsRemaining = 86 - currentAge;
      const totalAnnuityYears = 86 - age1;
      if (totalAnnuityYears <= 0) return 0;
      return annuityPurchaseAmount * (yearsRemaining / totalAnnuityYears);
    });

    return { portfolioBalance, investmentProperty, annuityValue, nonFinancialAssets };
  }, [results, formData]);

  if (!isOpen || !scenario || !results) return null;

  const age1 = getAge1(formData, results);
  const years = results.years || [];

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={scenario.name}
      headerStyle={{
        background: '#072040',
        borderBottom: '2px solid #072040',
        color: '#ffffff',
      }}
      titleStyle={{ color: '#ffffff' }}
      headerControls={
        <span style={{ fontSize: '13px', opacity: 0.8, color: '#fff' }}>
          Frozen on: {formatTimestamp(scenario.timestamp)}
        </span>
      }
    >
      {/* Body */}
      <div style={{ padding: '30px' }}>
        {/* Percentile selector */}
        <div className="card mb-3">
          <div className="card-header bg-light d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Retirement Projection</h5>
            <div style={{ width: '300px' }}>
              <label htmlFor="frozenPercentileSelect" className="form-label small mb-1">
                Income Chart Scenario:
              </label>
              <select
                className="form-select"
                id="frozenPercentileSelect"
                value={selectedPercentileIndex}
                onChange={(e) => setSelectedPercentileIndex(Number(e.target.value))}
              >
                {PERCENTILE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="card-body">
            {/* Row 1: Balance Fan Chart | Income Breakdown */}
            <div className="row">
              <div className="col-md-6">
                <div className="chart-container">
                  <BalanceFanChart
                    percentileData={results.balance_percentiles}
                    years={years}
                    percentiles={results.percentiles}
                    age1={age1}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="chart-container">
                  {incomeBreakdownData && (
                    <IncomeBreakdownChart
                      data={incomeBreakdownData}
                      years={years}
                      age1={age1}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Row 2: Household Wealth | Asset Class Allocation */}
            <div className="row mt-4">
              <div className="col-md-6">
                <div className="chart-container">
                  {householdWealthData && (
                    <HouseholdWealthChart
                      portfolioBalance={householdWealthData.portfolioBalance}
                      investmentProperty={householdWealthData.investmentProperty}
                      annuityValue={householdWealthData.annuityValue}
                      nonFinancialAssets={householdWealthData.nonFinancialAssets}
                      years={years}
                      age1={age1}
                    />
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="chart-container">
                  {results.asset_class_allocation && (
                    <AssetClassChart allocation={results.asset_class_allocation} />
                  )}
                </div>
              </div>
            </div>

            {/* Row 3: Portfolio Breakdown | Withdrawal Rate */}
            <div className="row mt-4">
              <div className="col-md-6">
                <div className="chart-container">
                  <PortfolioBreakdownChart
                    portfolioIncome={results.portfolio_income_percentiles?.[2] || []}
                    balance={results.balance_percentiles?.[2] || []}
                    yieldRate={results.portfolio_yield_rate || 0}
                    totalReturn={results.calculated_return || 0}
                    years={years}
                    age1={age1}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="chart-container">
                  <WithdrawalRateChart
                    portfolioIncome={results.portfolio_income_percentiles?.[2] || []}
                    balance={results.balance_percentiles?.[2] || []}
                    years={years}
                    age1={age1}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Portfolio Allocation Summary */}
        <PortfolioAllocationSummary
          weights={results.portfolio_weights}
          yieldRate={results.portfolio_yield_rate}
          growthExposure={results.portfolio_growth}
          expectedReturn={results.calculated_return}
          volatility={results.calculated_volatility}
        />

        {/* Summary HTML Section */}
        {results.summary && (
          <div className="card mb-3">
            <div className="mt-3 p-3">
              <div dangerouslySetInnerHTML={{ __html: results.summary }} />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '15px 30px',
        borderTop: '1px solid #E7E6E6',
        display: 'flex',
        justifyContent: 'flex-end',
        flexShrink: 0,
      }}>
        <button
          className="btn"
          style={{ backgroundColor: '#072040', color: '#ffffff' }}
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </ModalShell>
  );
};

export default FrozenScenarioModal;
